import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, TrendingUp, DollarSign, Car, ArrowRight, Shield, Clock, Users, Star, FileText, Phone, Briefcase, CreditCard, Globe, RefreshCw, Home as HomeIcon, HelpCircle, ChevronRight, Ban, AlertTriangle, Scale, Plane, Wallet, Laptop, ArrowUpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

import DeliveredSection from '../components/DeliveredSection';
import { ThreeStepsSection } from '../components/ThreeStepsSection';
import { ReferralSection } from '../components/ReferralSection';
import GoogleReviewsWidget from '../components/GoogleReviewsWidget';
import LeadMagnet from '../components/LeadMagnet';
import HeroCarousel from '../components/HeroCarousel';
import StatsBar from '../components/StatsBar';
import FeatureGrid from '../components/FeatureGrid';
import { useScrollReveal } from '../hooks/useAnimations';
import { Reveal, Words } from '../components/motion/Reveal';

const Home = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    document.title = language === 'en'
      ? 'Credit Auto Reset | 2nd & 3rd Chance Auto Financing Quebec'
      : 'Credit Auto Reset | Financement Auto 2e et 3e Chance au Québec';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      language === 'en'
        ? 'Refused by your bank? Discover your auto financing options in Quebec. Bad credit, bankruptcy, consumer proposal — free evaluation, no obligation.'
        : 'Refusé par votre banque? Découvrez vos options de financement auto au Québec. Mauvais crédit, faillite, proposition de consommateur — évaluation gratuite, sans obligation.'
    );
  }, [language]);

  // Situation cards data
  const situations = [
    {
      icon: <Ban className="w-5 h-5" strokeWidth={1.5} />,
      titleFr: 'Refus bancaire',
      titleEn: 'Bank Refusal',
      descFr: 'Votre banque a dit non? Nous avons accès à d\'autres options.',
      descEn: 'Your bank said no? We have access to other options.'
    },
    {
      icon: <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />,
      titleFr: 'Mauvais crédit',
      titleEn: 'Bad Credit',
      descFr: 'Un score bas ne vous disqualifie pas automatiquement.',
      descEn: 'A low score doesn\'t automatically disqualify you.'
    },
    {
      icon: <Scale className="w-5 h-5" strokeWidth={1.5} />,
      titleFr: 'Faillite / Proposition',
      titleEn: 'Bankruptcy / Proposal',
      descFr: 'Options possibles selon votre dossier et votre situation actuelle.',
      descEn: 'Options available depending on your file and current situation.'
    },
    {
      icon: <Plane className="w-5 h-5" strokeWidth={1.5} />,
      titleFr: 'Nouveau au Canada',
      titleEn: 'New to Canada',
      descFr: 'Peu ou pas d\'historique de crédit canadien? On peut vous aider.',
      descEn: 'Little or no Canadian credit history? We can help.'
    },
    {
      icon: <Wallet className="w-5 h-5" strokeWidth={1.5} />,
      titleFr: 'Aucun comptant',
      titleEn: 'No Down Payment',
      descFr: 'Des options sans mise de fonds peuvent exister selon votre dossier.*',
      descEn: 'Options without down payment may exist depending on your file.*'
    },
    {
      icon: <Laptop className="w-5 h-5" strokeWidth={1.5} />,
      titleFr: 'Travailleur autonome',
      titleEn: 'Self-Employed',
      descFr: 'Revenu non traditionnel? Nous comprenons votre réalité.',
      descEn: 'Non-traditional income? We understand your reality.'
    },
    {
      icon: <ArrowUpCircle className="w-5 h-5" strokeWidth={1.5} />,
      titleFr: 'Rebâtir le crédit',
      titleEn: 'Rebuild Credit',
      descFr: 'Un financement structuré peut contribuer à améliorer votre dossier.',
      descEn: 'Structured financing can contribute to improving your file.'
    }
  ];

  // How it works steps
  const steps = [
    {
      num: '1',
      titleFr: 'Faites votre demande en ligne',
      titleEn: 'Apply online',
      descFr: 'Envoyez votre demande de financement auto en quelques minutes grâce à notre formulaire sécurisé. Rapide, simple et 100\u00a0% confidentiel.',
      descEn: 'Send your car financing request in just a few minutes through our secure form. Fast, simple and 100% confidential.'
    },
    {
      num: '2',
      titleFr: 'Discutez avec un conseiller',
      titleEn: 'Talk with an advisor',
      descFr: 'Un spécialiste de notre équipe vous appelle pour peaufiner les détails de votre prêt auto et trouver la solution qui vous convient le mieux.',
      descEn: 'A specialist from our team calls you to fine-tune the details of your car loan and find the solution that suits you best.'
    },
    {
      num: '3',
      titleFr: 'Repartez avec vos clés',
      titleEn: 'Drive away with your keys',
      descFr: 'Une fois votre financement approuvé, choisissez votre véhicule dans notre inventaire, selon le montant accordé par la banque, et prenez la route sans attendre.',
      descEn: 'Once your financing is approved, choose your vehicle from our inventory, based on the amount approved by the bank, and hit the road right away.'
    }
  ];

  // FAQ items
  const faqItems = [
    {
      qFr: 'Est-ce que ma demande affecte mon dossier de crédit?',
      qEn: 'Does my application affect my credit file?',
      aFr: 'Notre évaluation initiale ne requiert pas d\'enquête de crédit. Une vérification formelle n\'est faite qu\'avec votre consentement, si vous décidez de poursuivre.',
      aEn: 'Our initial evaluation does not require a credit inquiry. A formal check is only done with your consent, if you decide to proceed.'
    },
    {
      qFr: 'Puis-je obtenir un financement après une faillite?',
      qEn: 'Can I get financing after bankruptcy?',
      aFr: 'Selon votre situation actuelle (faillite libérée ou en cours), des options de financement peuvent être disponibles. Chaque dossier est analysé individuellement.',
      aEn: 'Depending on your current situation (discharged or active bankruptcy), financing options may be available. Each file is analyzed individually.'
    },
    {
      qFr: 'Quel est le délai pour obtenir une réponse?',
      qEn: 'How long does it take to get an answer?',
      aFr: 'La plupart des demandes reçoivent une réponse dans les 24 heures ouvrables.',
      aEn: 'Most applications receive a response within 24 business hours.'
    },
    {
      qFr: 'Quels documents sont nécessaires?',
      qEn: 'What documents are needed?',
      aFr: 'Généralement : preuve d\'identité, preuve de revenu et preuve de résidence. Notre équipe vous guidera selon votre situation.',
      aEn: 'Generally: proof of identity, proof of income, and proof of residence. Our team will guide you based on your situation.'
    },
    {
      qFr: 'Est-ce que 0$ comptant est vraiment possible?',
      qEn: 'Is $0 down payment really possible?',
      aFr: 'Dans certains cas, un financement sans mise de fonds est possible, selon l\'approbation du prêteur et votre dossier de crédit. Ce n\'est pas garanti pour tous les dossiers.',
      aEn: 'In some cases, financing without a down payment is possible, subject to lender approval and your credit file. It is not guaranteed for all files.'
    }
  ];

  // Scroll reveal refs
  const situationRef = useScrollReveal();
  const whyRef = useScrollReveal();
  const howRef = useScrollReveal();
  const faqRef = useScrollReveal();
  const vehicleRef = useScrollReveal();
  const leadMagnetRef = useScrollReveal();

  return (
    <div className="min-h-screen">
      {/* ═══ HERO CAROUSEL ═══ */}
      <HeroCarousel />

      {/* ═══ ANIMATED STATS BAR ═══ */}
      <StatsBar />

      {/* Green Separator */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-[#1a5c3b]/30 to-transparent"></div>

      {/* ═══ FEATURE GRID ═══ */}
      <FeatureGrid />

      <DeliveredSection />

      {/* ═══ HOW WE'RE DIFFERENT ═══ */}
      <section ref={whyRef} className="reveal relative py-14 md:py-20 overflow-hidden bg-white" data-testid="why-car-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold text-[#1a5c3b] uppercase tracking-[0.2em] mb-4">
              {language === 'en' ? 'How we work' : 'Notre approche'}
            </p>
            <Words parts={[[language === 'en' ? 'What makes us different' : 'Ce qui nous distingue']]} className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] mb-4" />
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              {language === 'en'
                ? 'We don\'t just submit your file to one bank. We compare options across our entire network to find what works for you.'
                : 'On ne soumet pas votre dossier à une seule banque. On compare les options à travers tout notre réseau pour trouver ce qui fonctionne pour vous.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 */}
            <Reveal delay={0} className="bg-[#0b130e] rounded-2xl p-8 md:p-10 text-center group hover:scale-[1.02] transition-transform duration-300">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#1a5c3b] flex items-center justify-center text-white">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                {language === 'en' ? 'Real Human Analysis' : 'Analyse par un vrai humain'}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {language === 'en'
                  ? 'No algorithm decides your future. A real credit analyst reviews your full profile, not just a number.'
                  : 'Aucun algorithme ne décide de votre avenir. Un vrai analyste de crédit examine votre profil complet, pas juste un chiffre.'}
              </p>
            </Reveal>
            {/* Card 2 */}
            <Reveal delay={150} className="bg-[#0b130e] rounded-2xl p-8 md:p-10 text-center group hover:scale-[1.02] transition-transform duration-300">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#1a5c3b] flex items-center justify-center text-white">
                <TrendingUp className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                {language === 'en' ? '20+ Lender Network' : '20+ prêteurs dans notre réseau'}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {language === 'en'
                  ? 'One application, multiple options. We shop your file across our network to find the best terms available for your situation.'
                  : 'Une seule demande, plusieurs options. On magasine votre dossier à travers notre réseau pour trouver les meilleures conditions disponibles.'}
              </p>
            </Reveal>
            {/* Card 3 */}
            <Reveal delay={300} className="bg-[#0b130e] rounded-2xl p-8 md:p-10 text-center group hover:scale-[1.02] transition-transform duration-300">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#1a5c3b] flex items-center justify-center text-white">
                <Shield className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                {language === 'en' ? 'One Visit, That\'s It' : 'Un seul déplacement, c\'est tout'}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {language === 'en'
                  ? 'Everything is handled remotely. You only come once — to pick up your vehicle. Located in Montreal, Île-des-Sœurs.'
                  : 'Tout se fait à distance. Vous vous déplacez une seule fois — pour prendre possession de votre véhicule. Situé à Montréal, Île-des-Sœurs.'}
              </p>
            </Reveal>
          </div>
          <div className="text-center mt-10">
            <Link to="/demander-un-pret-auto">
              <Button size="lg" className="bg-[#1a5c3b] hover:bg-[#165432] text-white px-10 py-5 text-base font-bold shadow-xl uppercase tracking-wide transition-all hover:scale-105">
                {language === 'en' ? 'APPLY NOW' : 'FAIRE UNE DEMANDE'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <div ref={howRef} data-testid="how-it-works">
        <ThreeStepsSection steps={steps} ctaLabel={t.hero.cta} />
      </div>

      {/* ═══ PROOF: Google Reviews + Delivered Vehicles ═══ */}
      <GoogleReviewsWidget />

      {/* Dark CTA Band */}
      <div className="py-12 bg-[#0b130e] text-center">
        <p className="text-xl text-gray-300 mb-5 font-light">
          {language === 'en' ? 'Ready to explore your options?' : 'Prêt à explorer vos options\u00a0?'}
        </p>
        <Link to="/demander-un-pret-auto">
          <Button size="lg" className="bg-[#1a5c3b] hover:bg-[#165432] text-white px-10 py-5 text-base font-bold shadow-xl uppercase tracking-wide transition-all hover:scale-105">
            {language === 'en' ? 'APPLY NOW' : 'FAIRE UNE DEMANDE'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </div>

      {/* ═══ FINANCING PARTNERS ═══ */}
      <section className="py-14 md:py-20 bg-[#0b130e] overflow-hidden" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold text-green-400 uppercase tracking-[0.2em] mb-4">
              {language === 'en' ? 'Our network' : 'Notre réseau'}
            </p>
            <Words parts={[[t.partners.title]]} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3" />
            <p className="text-lg text-gray-400 max-w-xl mx-auto">
              {language === 'en' 
                ? 'One application, multiple lenders. We compare to find you the best terms.' 
                : 'Une seule demande, plusieurs prêteurs. On compare pour vous trouver les meilleures conditions.'}
            </p>
          </div>

          {/* Partner logos - clean display on white strip */}
          <div className="max-w-5xl mx-auto bg-white rounded-2xl p-8 md:p-10 shadow-xl">
            <img
              src="/images/partners.png"
              alt={language === 'en' ? 'Financial partners - Desjardins TD Scotiabank iA EdenPark Santander Iceberg Finance' : 'Partenaires financiers - Desjardins TD Scotiabank iA EdenPark Santander Iceberg Finance'}
              className="w-full h-auto"
              loading="lazy"
            />
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            {language === 'en' ? '+ many other specialized lenders' : '+ plusieurs autres prêteurs spécialisés'}
          </p>
        </div>
      </section>

      {/* CTA after Partners */}
      <div className="py-10 bg-[#0b130e] text-center">
        <Link to="/demander-un-pret-auto">
          <Button size="lg" className="bg-[#1a5c3b] hover:bg-[#165432] text-white px-10 py-5 text-base font-bold shadow-xl uppercase tracking-wide transition-all hover:scale-105">
            {language === 'en' ? 'APPLY NOW' : 'FAIRE UNE DEMANDE'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </div>

      {/* ═══ VEHICLE ACCESS ═══ */}
      <section ref={vehicleRef} className="reveal relative py-14 md:py-20 overflow-hidden" data-testid="vehicle-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b130e] via-[#1a3d2a] to-[#0b130e]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="text-center mb-10">
            <Words parts={[[language === 'en' ? 'Vehicles for Every Budget' : 'Véhicules pour tous les budgets']]} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3" />
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Once your financing is approved, choose from our selection of quality vehicles.' 
                : 'Une fois votre financement approuvé, choisissez parmi notre sélection de véhicules de qualité.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              <img src="https://images.pexels.com/photos/11501948/pexels-photo-11501948.jpeg?auto=compress&cs=tinysrgb&w=800" alt={language === 'en' ? 'Sedan financing Quebec' : 'Financement berline Québec'} className="w-full h-60 object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">{language === 'en' ? 'Sedans' : 'Berlines'}</h3>
                  <p className="text-green-200 text-sm">{language === 'en' ? 'Reliable & Affordable' : 'Fiable et abordable'}</p>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              <img src="https://images.unsplash.com/photo-1688715680251-153b3e0ad21c?crop=entropy&cs=srgb&fm=jpg&w=800&q=85" alt={language === 'en' ? 'SUV financing Quebec' : 'Financement VUS Québec'} className="w-full h-60 object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">VUS</h3>
                  <p className="text-green-200 text-sm">{language === 'en' ? 'Family-Friendly' : 'Familial et spacieux'}</p>
                </div>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
              <img src="https://images.unsplash.com/photo-1571986929789-95307bbfa7c2?crop=entropy&cs=srgb&fm=jpg&w=800&q=85" alt={language === 'en' ? 'Pickup truck financing Quebec' : 'Financement camion Québec'} className="w-full h-60 object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent flex items-end p-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-1">{language === 'en' ? 'Pickups' : 'Camions'}</h3>
                  <p className="text-green-200 text-sm">{language === 'en' ? 'Powerful & Reliable' : 'Puissant et fiable'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA after Vehicles */}
      <div className="py-12 bg-[#0b130e] text-center">
        <p className="text-lg text-slate-400 text-base mb-5 font-light">
          {language === 'en' ? 'Whatever your situation, adapted financing is possible.' : 'Peu importe vos difficultés, un financement adapté à votre situation est possible.'}
        </p>
        <Link to="/demander-un-pret-auto">
          <Button size="lg" className="bg-[#1a5c3b] hover:bg-[#165432] text-white px-10 py-5 text-base font-bold shadow-xl uppercase tracking-wide transition-all hover:scale-105">
            {language === 'en' ? 'APPLY NOW' : 'FAIRE UNE DEMANDE'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </div>

      {/* ═══ FAQ SECTION ═══ */}
      <section ref={faqRef} className="reveal py-14 md:py-20 bg-white" data-testid="home-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Words parts={[[language === 'en' ? 'Frequently Asked Questions' : 'Questions fréquentes']]} className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0f172a] mb-4" />
          </div>
          <div className="space-y-4">
            {faqItems.map((item, i) => (
              <Reveal key={i} delay={i * 80}><details className="group bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-50 transition-colors">
                  <span className="font-semibold text-[#0f172a] text-sm md:text-base pr-4">
                    {language === 'en' ? item.qEn : item.qFr}
                  </span>
                  <HelpCircle className="w-5 h-5 text-slate-400 group-open:text-[#1a5c3b] transition-colors flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6 text-base text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                  {language === 'en' ? item.aEn : item.aFr}
                </div>
              </details></Reveal>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/faq" className="text-[#1a5c3b] font-semibold hover:underline text-sm">
              {language === 'en' ? 'See all questions →' : 'Voir toutes les questions →'}
            </Link>
          </div>

          {/* CTA after FAQ */}
          <div className="text-center mt-10 pt-8 border-t border-gray-200">
            <p className="text-gray-600 text-sm mb-4">
              {language === 'en' ? 'Still have questions? We\'re here to help.' : 'Encore des questions? On est là pour vous aider.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link to="/demander-un-pret-auto">
                <Button className="bg-[#1a5c3b] hover:bg-[#165432] text-white px-6 py-4 text-sm font-semibold shadow-md hover:shadow-lg transition-all">
                  {t.hero.cta}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="tel:+15149000886">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:border-[#1a5c3b] hover:text-[#1a5c3b] px-6 py-4 text-sm font-semibold transition-all">
                  <Phone className="mr-2 w-4 h-4" />
                  {t.hero.ctaSecondary}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ LEAD MAGNET ═══ */}
      <LeadMagnet />

      <ReferralSection />

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#1a5c3b] via-[#165432] to-green-800 relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Words parts={[[language === 'en' ? 'Ready to discover your options?' : 'Prêt à découvrir vos options\u00a0?']]} className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" />
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            {t.hero.trustLine}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/demander-un-pret-auto">
              <Button size="lg" className="bg-white text-[#1a5c3b] hover:bg-gray-100 px-8 py-6 text-lg font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300" data-testid="final-cta-button">
                {t.hero.cta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+15149000886">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1a5c3b] px-8 py-6 text-lg font-semibold transition-all duration-300">
                <Phone className="mr-2 w-5 h-5" />
                {t.hero.ctaSecondary}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
